/**
 * Cache Utilities Module - IPHUB Project
 * Helper functions for cache operations
 */

import { ERROR_MESSAGES, DEBUG_MESSAGES } from "./config.js";

/**
 * Check if localStorage is available
 * @returns {boolean} Whether localStorage is available
 */
export function checkStorageAvailability() {
  try {
    const test = "__cache_test__";
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch (e) {
    console.warn(ERROR_MESSAGES.STORAGE_UNAVAILABLE);
    return false;
  }
}

/**
 * Calculate current storage size for a given prefix
 * @param {string} prefix - Storage prefix to calculate size for
 * @returns {number} Total size in bytes
 */
export function calculateCurrentSize(prefix) {
  if (!checkStorageAvailability()) return 0;

  let totalSize = 0;

  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith(prefix)) {
      const value = localStorage.getItem(key);
      if (value) {
        totalSize += (key.length + value.length) * 2; // UTF-16
      }
    }
  }

  return totalSize;
}

/**
 * Generate cache key with prefix and version
 * @param {string} key - Original key
 * @param {string} prefix - Optional prefix for namespace
 * @param {string} basePrefix - Base prefix from config
 * @param {string} version - Cache version
 * @returns {string} Generated cache key
 */
export function generateKey(key, prefix = "", basePrefix = "iphub_", version = "2.0") {
  const fullPrefix = prefix ? `${basePrefix}${prefix}_` : basePrefix;
  return `${fullPrefix}v${version}_${key}`;
}

/**
 * Create cache item with metadata
 * @param {any} data - Data to cache
 * @param {number|null} ttl - Time to live in milliseconds
 * @param {string} version - Cache version
 * @returns {Object} Cache item
 */
export function createCacheItem(data, ttl, version = "2.0") {
  return {
    data: data,
    timestamp: Date.now(),
    ttl: ttl,
    version: version,
  };
}

/**
 * Check if cache item is expired
 * @param {Object} item - Cache item to check
 * @returns {boolean} Whether item is expired
 */
export function isExpired(item) {
  if (!item || !item.timestamp || !item.ttl) return false; // null TTL = persistent
  return Date.now() - item.timestamp > item.ttl;
}

/**
 * Calculate item size in bytes
 * @param {string} key - Cache key
 * @param {any} data - Data to calculate size for
 * @returns {number} Size in bytes
 */
export function calculateItemSize(key, data) {
  try {
    const jsonString = JSON.stringify(data);
    return (key.length + jsonString.length) * 2; // UTF-16
  } catch (error) {
    return 0;
  }
}

/**
 * Format debug message with placeholders
 * @param {string} template - Message template with {placeholder}
 * @param {Object} values - Values to replace placeholders
 * @returns {string} Formatted message
 */
export function formatDebugMessage(template, values = {}) {
  return template.replace(/\{(\w+)\}/g, (match, key) => {
    return values[key] !== undefined ? values[key] : match;
  });
}

/**
 * Log debug message if debug is enabled
 * @param {string} message - Message to log
 * @param {boolean} debug - Whether debug is enabled
 */
export function logDebug(message, debug = false) {
  if (debug && typeof console !== "undefined" && console.debug) {
    console.debug(message);
  }
}

/**
 * Log warning message
 * @param {string} message - Message to log
 */
export function logWarning(message) {
  if (typeof console !== "undefined" && console.warn) {
    console.warn(message);
  }
}

/**
 * Log error message
 * @param {string} message - Message to log
 * @param {Error} error - Error object
 */
export function logError(message, error = null) {
  if (typeof console !== "undefined" && console.error) {
    if (error) {
      console.error(message, error);
    } else {
      console.error(message);
    }
  }
}

/**
 * Collect all cache keys with a given prefix
 * @param {string} prefix - Prefix to filter by
 * @returns {Array<string>} Array of cache keys
 */
export function collectCacheKeys(prefix) {
  const keys = [];

  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith(prefix)) {
      keys.push(key);
    }
  }

  return keys;
}

/**
 * Remove cache keys from localStorage
 * @param {Array<string>} keys - Keys to remove
 * @returns {number} Number of keys removed
 */
export function removeCacheKeys(keys) {
  let removedCount = 0;

  keys.forEach((key) => {
    try {
      localStorage.removeItem(key);
      removedCount++;
    } catch (error) {
      logError(`Failed to remove cache key: ${key}`, error);
    }
  });

  return removedCount;
}

/**
 * Parse cache item from localStorage
 * @param {string} serializedItem - Serialized cache item
 * @returns {Object|null} Parsed cache item or null if invalid
 */
export function parseCacheItem(serializedItem) {
  try {
    return JSON.parse(serializedItem);
  } catch (error) {
    logError(ERROR_MESSAGES.CORRUPTED_DATA, error);
    return null;
  }
}

/**
 * Validate cache item structure
 * @param {Object} item - Cache item to validate
 * @returns {boolean} Whether item is valid
 */
export function validateCacheItem(item) {
  return (
    item && typeof item === "object" && "data" in item && "timestamp" in item && "version" in item
  );
}

/**
 * Get cache statistics from localStorage
 * @param {string} prefix - Prefix to calculate stats for
 * @returns {Object} Cache statistics
 */
export function getCacheStats(prefix) {
  const keys = collectCacheKeys(prefix);
  const totalSize = calculateCurrentSize(prefix);

  return {
    itemCount: keys.length,
    totalSize,
    averageSize: keys.length > 0 ? totalSize / keys.length : 0,
  };
}
